import type { RecordLikeProtocol } from '@lighthouse/core'
import { buildPageVariableDependencies, FlowExecutor, resolveFilter, useAtomAction, useAtomData } from '@lighthouse/shared'
import { useCallback } from 'react'

import { getRecordByFilterAtom } from '@/atoms/dataSource/action'
import { pageVariableRecordMapAtom, pageVariableSchemasFamily } from '@/atoms/page/state'
import { useCurrentAppId, useCurrentEnvId } from '@/hooks/useApplication'
import { useParseVariableWithSubscription } from '@/hooks/useParseVariable'

/** 获取页面变量对应的record */
export const useFetchPageVariable = ({ pageId, stackId }: { pageId: string; stackId: string }) => {
    const pageVariableSchemas = useAtomData(pageVariableSchemasFamily(pageId))

    const appId = useCurrentAppId()
    const envId = useCurrentEnvId()
    const parseVariable2Field = useParseVariableWithSubscription(undefined, { pageId, stackId })
    const { run: setPageVariableValues } = useAtomAction(pageVariableRecordMapAtom)
    const { run: getRecordByFilter } = useAtomAction(getRecordByFilterAtom)
    return useCallback(async () => {
        const records: RecordLikeProtocol[] = []

        if (!appId || !envId) {
            return records
        }

        const nodes = buildPageVariableDependencies(pageVariableSchemas ?? [])
        const startNodes = [...nodes.entries()].filter(([_, node]) => !node.left || node.left.length === 0).map(([id]) => id)
        const executor = new FlowExecutor(nodes, startNodes, async node => {
            const { data: variable } = node
            const res = await getRecordByFilter({
                appId,
                envId,
                ...variable,
                filter: resolveFilter({ filter: variable.filter, extraParams: { parseVariable2Field } })
            })

            if (res) {
                records.push(res)
            }
            setPageVariableValues(s => ({
                ...s,
                [stackId]: { ...s[stackId], [variable.id]: res ? { appId, envId, dsId: variable.dsId, recordId: res.id } : undefined }
            }))

            if (res) {
                return { type: 'record', success: true, data: res }
            }

            return { type: 'record', success: false }
        })

        await executor.run()

        return records
    }, [appId, envId, getRecordByFilter, pageVariableSchemas, parseVariable2Field, setPageVariableValues, stackId])
}
